/**
 * YG Bottle Washer Match Quiz
 * Dynamic multi-step questionnaire with configurable result-key scores.
 */
;(function () {
  const ELEMENT_NAME = 'yg-bottle-washer-match-quiz'

  class YGBottleWasherMatchQuiz extends HTMLElement {
    constructor() {
      super()
      this.answers = []
      this.currentQuestionIndex = 0
      this.advanceTimer = null
      this.handleClick = this.handleClick.bind(this)
      this.handleBlockSelect = this.handleBlockSelect.bind(this)
    }

    connectedCallback() {
      this.quizView = this.querySelector('[data-quiz-view]')
      this.resultView = this.querySelector('[data-quiz-result-view]')
      this.heading = this.querySelector('[data-quiz-heading]')
      this.progress = this.querySelector('[data-quiz-progress]')
      this.progressFill = this.querySelector('[data-quiz-progress-fill]')
      this.progressCount = this.querySelector('[data-quiz-progress-count]')
      this.questions = Array.from(this.querySelectorAll('[data-quiz-question]'))
      this.results = Array.from(this.querySelectorAll('[data-quiz-result]'))
      this.noResult = this.querySelector('[data-quiz-no-result]')
      this.autoAdvanceDelay = Number(this.dataset.autoAdvanceDelay || 180)

      this.addEventListener('click', this.handleClick)

      if (window.Shopify && window.Shopify.designMode) {
        document.addEventListener('shopify:block:select', this.handleBlockSelect)
      }

      if (this.questions.length > 0) {
        this.showQuestion(0, false)
      }
    }

    disconnectedCallback() {
      window.clearTimeout(this.advanceTimer)
      this.removeEventListener('click', this.handleClick)
      document.removeEventListener('shopify:block:select', this.handleBlockSelect)
    }

    handleClick(event) {
      const option = event.target.closest('[data-quiz-option]')
      if (option && this.contains(option)) {
        this.selectAnswer(option)
        return
      }

      const backButton = event.target.closest('[data-quiz-back]')
      if (backButton && this.contains(backButton)) {
        this.goBack()
        return
      }

      const retakeButton = event.target.closest('[data-quiz-retake]')
      if (retakeButton && this.contains(retakeButton)) {
        this.retakeQuiz()
      }
    }

    selectAnswer(option) {
      const question = option.closest('[data-quiz-question]')
      const questionIndex = Number(question?.dataset.questionIndex)
      const answerValue = option.dataset.answerValue
      const resultWeights = this.parseResultWeights(option.dataset.resultKeys)

      if (!question || !Number.isInteger(questionIndex) || !answerValue) {
        return
      }

      window.clearTimeout(this.advanceTimer)
      this.answers = this.answers.slice(0, questionIndex)
      this.answers[questionIndex] = {
        value: answerValue,
        resultWeights,
      }
      this.clearQuestionsAfter(questionIndex)
      this.setQuestionSelection(question, answerValue)

      this.advanceTimer = window.setTimeout(() => {
        if (questionIndex < this.questions.length - 1) {
          this.showQuestion(questionIndex + 1)
        } else {
          this.showResult()
        }
      }, this.prefersReducedMotion() ? 0 : this.autoAdvanceDelay)
    }

    goBack() {
      window.clearTimeout(this.advanceTimer)

      if (this.currentQuestionIndex <= 0) {
        return
      }

      this.showQuestion(this.currentQuestionIndex - 1)
    }

    retakeQuiz() {
      window.clearTimeout(this.advanceTimer)
      this.answers = []
      this.currentQuestionIndex = 0
      this.questions.forEach((question) => this.setQuestionSelection(question, null))
      this.hideAllResults()
      this.showQuestion(0, false)

      window.requestAnimationFrame(() => {
        this.heading?.focus({ preventScroll: true })
        this.scrollIntoView({ behavior: this.prefersReducedMotion() ? 'auto' : 'smooth', block: 'start' })
      })
    }

    showQuestion(index, shouldFocus = true) {
      if (!this.questions[index] || !this.quizView || !this.resultView) {
        return
      }

      this.currentQuestionIndex = index
      this.quizView.hidden = false
      this.resultView.hidden = true
      this.classList.remove('is-result')

      this.questions.forEach((question, questionIndex) => {
        question.hidden = questionIndex !== index
      })

      this.setQuestionSelection(this.questions[index], this.answers[index]?.value || null)
      this.updateProgress(index, false)

      if (shouldFocus) {
        window.requestAnimationFrame(() => {
          this.questions[index].querySelector('[data-question-title]')?.focus({ preventScroll: true })
        })
      }
    }

    showResult() {
      if (!this.quizView || !this.resultView) {
        return
      }

      const scores = new Map()

      this.answers.slice(0, this.questions.length).forEach((answer) => {
        answer?.resultWeights?.forEach((weight, resultKey) => {
          scores.set(resultKey, (scores.get(resultKey) || 0) + weight)
        })
      })

      let matchedResult = null
      let highestScore = 0

      this.results.forEach((result) => {
        const resultKey = this.normalizeResultKey(result.dataset.resultKey)
        const score = resultKey ? scores.get(resultKey) || 0 : 0

        result.dataset.resultScore = String(score)

        if (score > highestScore) {
          highestScore = score
          matchedResult = result
        }
      })

      const fallbackResult = this.results.find((result) => result.dataset.fallback === 'true')

      if (!matchedResult) {
        matchedResult = fallbackResult || null
      }

      this.quizView.hidden = true
      this.resultView.hidden = false
      this.classList.add('is-result')
      this.updateProgress(this.questions.length, true)
      this.hideAllResults()

      if (matchedResult) {
        matchedResult.hidden = false
        window.requestAnimationFrame(() => {
          matchedResult.querySelector('[data-result-title]')?.focus({ preventScroll: true })
        })
      } else if (this.noResult) {
        this.noResult.hidden = false
        this.noResult.querySelector('[data-quiz-retake]')?.focus({ preventScroll: true })
      }
    }

    hideAllResults() {
      this.results.forEach((result) => {
        result.hidden = true
      })

      if (this.noResult) {
        this.noResult.hidden = true
      }
    }

    setQuestionSelection(question, selectedValue) {
      question.querySelectorAll('[data-quiz-option]').forEach((option) => {
        const isSelected = selectedValue !== null && option.dataset.answerValue === String(selectedValue)
        option.classList.toggle('is-selected', isSelected)
        option.setAttribute('aria-pressed', String(isSelected))
      })
    }

    clearQuestionsAfter(questionIndex) {
      this.questions.forEach((question, index) => {
        if (index > questionIndex) {
          this.setQuestionSelection(question, null)
        }
      })
    }

    updateProgress(stepIndex, isComplete) {
      const total = this.questions.length
      const completedCount = Math.min(stepIndex, total)
      const progressPercent = total > 0 ? (completedCount / total) * 100 : 0
      const visibleStep = isComplete ? total : Math.min(stepIndex + 1, total)

      if (this.progressFill) {
        this.progressFill.style.width = `${progressPercent}%`
      }

      if (this.progressCount) {
        this.progressCount.textContent = `${visibleStep}/${total}`
      }

      if (this.progress) {
        this.progress.setAttribute('aria-valuenow', String(completedCount))
        this.progress.setAttribute(
          'aria-valuetext',
          isComplete ? `Completed ${total} of ${total}` : `Question ${visibleStep} of ${total}`,
        )
      }
    }

    parseResultWeights(value) {
      const weights = new Map()

      String(value || '')
        .split(/[,，、/;；\r\n]+/)
        .forEach((entry) => {
          const match = entry.trim().match(/^([^:：]+?)(?:[:：]\s*\+?(\d+))?$/)
          if (!match) return

          const resultKey = this.normalizeResultKey(match[1])
          const weight = match[2] === undefined ? 1 : Number(match[2])
          if (resultKey && Number.isSafeInteger(weight)) {
            weights.set(resultKey, weight)
          }
        })

      return weights
    }

    normalizeResultKey(value) {
      return String(value || '').trim().toLowerCase()
    }

    handleBlockSelect(event) {
      const block = event.target

      if (!(block instanceof HTMLElement) || !this.contains(block)) {
        return
      }

      const question = block.closest('[data-quiz-question]')
      if (question) {
        this.showQuestion(Number(question.dataset.questionIndex), false)
        return
      }

      const result = block.closest('[data-quiz-result]')
      if (result && this.quizView && this.resultView) {
        this.quizView.hidden = true
        this.resultView.hidden = false
        this.classList.add('is-result')
        this.hideAllResults()
        result.hidden = false
      }
    }

    prefersReducedMotion() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches
    }
  }

  if (!customElements.get(ELEMENT_NAME)) {
    customElements.define(ELEMENT_NAME, YGBottleWasherMatchQuiz)
  }
})()
